// Cliente API — fetch wrapper com cookie de sessão (credentials) e erro tipado
export type ApiError = { status: number; message: string; details?: Record<string, string[]> };

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  if (res.status === 401) {
    // 401 só expulsa para o login DENTRO do painel autenticado.
    // Páginas públicas (landing) e o próprio /auth/me tratam 401 como "sem sessão".
    const p = location.pathname;
    if (p.startsWith('/painel') && p !== '/painel/login') {
      location.href = '/painel/login';
    }
    throw { status: 401, message: 'Não autenticado' } as ApiError;
  }
  if (!res.ok) {
    let body: { error?: string; details?: Record<string, string[]> } = {};
    try {
      body = await res.json();
    } catch {
      /* respostas não-JSON (HTML de erro etc.) */
    }
    throw {
      status: res.status,
      message: body.error ?? `Erro ${res.status}`,
      details: body.details,
    } as ApiError;
  }
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body !== undefined ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  multipart: <T>(path: string, formData: FormData) =>
    fetch(`/api${path}`, { method: 'POST', credentials: 'include', body: formData }).then(async (res) => {
      if (!res.ok) throw { status: res.status, message: `Erro ${res.status}` } as ApiError;
      return res.json() as Promise<T>;
    }),
};

export type PublicUser = {
  id: string;
  email: string;
  fullName: string;
  role: 'admin' | 'gestor' | 'vendedor';
  isActive: boolean;
};

export const ROLE_LABEL: Record<PublicUser['role'], string> = {
  admin: 'Admin',
  gestor: 'Gestor de vendas',
  vendedor: 'Vendedor',
};